// The one place money amounts are formatted, and where an item's EUR price is derived from its USD price. The money
// service (backend/money), the website, the store and the launcher (a Rust copy in launcher/src/price.rs, tested
// against the same vectors: sdk/purchase/test/format-vectors.json) all use these; never format an amount elsewhere.
//
// Every amount is integer minor units of a currency: `{amount_minor: 499, currency: "USD"}` is $4.99 (CONTRACTS.md
// wave 10 "Amounts"). Currencies at launch: USD and EUR.

/** Wallet and price currencies at launch. */
export const CURRENCIES = ["USD", "EUR"] as const;
export type Currency = (typeof CURRENCIES)[number];

export const isCurrency = (c: unknown): c is Currency => typeof c === "string" && (CURRENCIES as readonly string[]).includes(c);

/** An amount as the money API sends it (`formatted` only in responses). */
export interface Money {
  amount_minor: number;
  currency: string;
  formatted?: string;
}

/** An amount as the purchase SDK hands it to games (camelCase, with the display string). */
export interface SdkMoney {
  amountMinor: number;
  currency: string;
  display: string;
}

const SYMBOLS: Record<string, string> = { USD: "$", EUR: "€" };

/** Thousands separators without Intl, so the output is identical in every runtime (and in the Rust copy). */
function group(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * `$4.99`, `€4.99`, `-$1.00`, `$1,234.56`: symbol first, minus before the symbol, two decimals, comma thousands, no
 * locale. Throws on a non-integer amount or a currency other than USD and EUR (codes are upper case).
 */
export function formatMoney(amountMinor: number, currency: string): string {
  if (!Number.isSafeInteger(amountMinor)) throw new RangeError(`amount must be an integer number of minor units, got ${amountMinor}`);
  const sign = amountMinor < 0 ? "-" : "";
  const abs = Math.abs(amountMinor);
  const num = `${group(Math.floor(abs / 100))}.${String(abs % 100).padStart(2, "0")}`;
  const sym = Object.prototype.hasOwnProperty.call(SYMBOLS, currency) ? SYMBOLS[currency] : undefined;
  if (!sym) throw new RangeError(`unknown currency ${String(currency)}`);
  return `${sign}${sym}${num}`;
}

/** A `Money` with its `formatted` string. */
export function money(amountMinor: number, currency: string): Required<Money> {
  return { amount_minor: amountMinor, currency, formatted: formatMoney(amountMinor, currency) };
}

/** The SDK's camelCase form of a money API amount (`{amount_minor, currency, formatted?}`). */
export function toSdkMoney(m: { amount_minor: number; currency: string; formatted?: string }): SdkMoney {
  return { amountMinor: m.amount_minor, currency: m.currency, display: m.formatted ?? formatMoney(m.amount_minor, m.currency) };
}

// ---------------------------------------------------------------- the EUR price ladder

/**
 * The fixed rate the EUR price ladder converts at: US dollars per euro in basis points, from the dated FX snapshot in
 * backend/money/src/pricing.ts (`FX_TABLE_DATE`). It is part of the ladder, not live FX: changing it reprices every
 * item without a creator's EUR override, so do it on purpose (and update the vectors).
 */
export const LADDER_USD_PER_EUR_BPS = 11600; // 1.16
export const LADDER_DATE = "2026-10-16";

/**
 * A friendly price at or above `minor` (2-decimal currency): under 10.00 in steps of 0.10 ending in 9 (4.39, 8.69),
 * then steps of 0.50 (12.49, 12.99), 5 (124.99), 50 (1,249.99)… Overshoot is at most about 5% from 10.00 up, 10% just
 * above 1.00.
 */
export function friendlyCeil(minor: number): number {
  const major = Math.max(minor, 1) / 100;
  const digits = Math.max(1, Math.floor(Math.log10(major)) + 1);
  const step = Math.max(1, Math.round(100 * (digits === 1 ? 0.1 : 0.5 * 10 ** (digits - 2))));
  const ending = step > 1 ? 1 : 0;
  return Math.ceil((minor + ending) / step) * step - ending;
}

/**
 * An item's EUR price from its USD price (CONTRACTS.md wave 10 "Prices": the creator sets USD; EUR comes from this
 * ladder unless the creator overrides it). Converts at the ladder rate, rounding up; under €1.00 that is the price,
 * from €1.00 up it is rounded up to a friendly price (`friendlyCeil`). Never below 5 (€0.05), like the USD minimum.
 * Prices are tax-inclusive in both currencies.
 */
export function eurFromUsd(usdMinor: number): number {
  if (!Number.isSafeInteger(usdMinor) || usdMinor <= 0) throw new RangeError(`a USD price must be a positive integer, got ${usdMinor}`);
  const eur = Math.ceil((usdMinor * 10000) / LADDER_USD_PER_EUR_BPS);
  return Math.max(5, eur < 100 ? eur : friendlyCeil(eur));
}

/** An item's price in `currency`: USD as set, EUR as overridden or from the ladder. */
export function priceIn(currency: Currency, usdMinor: number, eurOverride?: number | null): number {
  return currency === "USD" ? usdMinor : (eurOverride ?? eurFromUsd(usdMinor));
}
